import { type JSX,useCallback, useState } from 'react';

import {
    GridCallbackDetails,
    GridRowId,
    GridRowModel,
    GridRowSpacingParams,
    GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch } from 'app/hooks';
import { setCurrentEntityType, setDeleteCurrentTenantId, setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { openDrawer, setCurrentDrawerId, setCurrentEntity, setCurrentOperation, setCurrentTenantId, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { PaginationMode } from 'common/constants/paginationMode';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    DeleteDialogContentProps,
    DrawerEntitiesNames,
    EditDrawerContentProps,
    Operations } from 'common/types/TypeDefs';
import {
    getActionColumn,
    getChipColumn,
    getChipsColumn,
    getDateTimeTooltipColumn,
    getFirstColumn,
    getTooltipColumn } from 'common/utils/ColumnGenerator';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';

import { StyledDataGrid } from '../PalmDataGrid/StyledDataGrid';

import { CustomNoRowsOverlay } from './CustomNoRowsOverlay';

type UserProfilesDataGridProps<T, K> = {
    tenant: IUserProfileTenant,
    rows: GridRowModel[],
    contentFunction?: (props: EditDrawerContentProps<T>) => JSX.Element | null;
    deleteFunction?: (props: DeleteDialogContentProps<K>) => JSX.Element | null;
    rowClick?: (id: string, row?: GridRowModel) => void;
    openLicense?: (id: string, row?: GridRowModel) => void;
    openUserGroup?: (id: string, row?: GridRowModel) => void;
    actionDefinitions?: ActionDefinition[];
    columnDefinitions?: ColumnDefinition[];
    processing: boolean;
    customDeleteTitle?: string;
    title?: string;
    noRowsOverlayCustomTitle?: string;
    customCreateEditTitle?: string;
    currentEntityType?: DrawerEntitiesNames;
    clientSortHandler?: (model: GridSortModel, details: GridCallbackDetails) => void;
    sortModel?: GridSortModel;
    rowLink?: (id: string, row?: GridRowModel) => string,
};

export function UserProfilesDataGrid<T, K>({
    tenant,
    rows = [],
    contentFunction,
    deleteFunction,
    rowClick,
    actionDefinitions,
    columnDefinitions,
    title,
    processing,
    customDeleteTitle,
    noRowsOverlayCustomTitle,
    customCreateEditTitle,
    currentEntityType,
    clientSortHandler,
    sortModel = []
}: Readonly<UserProfilesDataGridProps<T, K>>): JSX.Element {
    const [editingRow, setEditingRow] = useState<T | K>();
    const [editingItemId, setEditingItemId] = useState<GridRowId>('');

    const dispatch = useAppDispatch();
    const actionCallback = useCallback(
        (id: GridRowId, row: GridRowModel, rowAction: ActionDefinition) => () => {
            if (typeof rowAction.disabled === 'function' ? rowAction.disabled(row) : rowAction.disabled) {
                return;
            }
            setEditingItemId(id);
            setEditingRow(row as T | K);

            switch ( rowAction.action ) {
                case Operations.Edit:
                    dispatch(setCurrentDrawerId(currentEntityType + 'Id'));
                    dispatch(openDrawer());
                    dispatch(setCurrentOperation(rowAction.action));
                    dispatch(setCurrentEntity(currentEntityType));
                    dispatch(setCurrentTenantId(tenant.id));
                    dispatch(setSaveButtonEnabled(true));
                    break;
                case Operations.Delete:
                    dispatch(setCurrentEntityType(currentEntityType));
                    dispatch(setDeleteCurrentTenantId(tenant.id));
                    dispatch(setDialogOpen(true));
                    break;
            }
        },
        []
    );

    const columns = columnDefinitions?.map(colDef => {
        switch (colDef.columnType) {
            case ColumnType.First:
                return getFirstColumn(colDef, false, rows || []);
            case ColumnType.Chip:
                return getChipColumn(colDef);
            case ColumnType.Chips:
                return getChipsColumn(colDef);
            case ColumnType.Action:
                return getActionColumn(colDef, actionDefinitions ?? [], actionCallback);
            case ColumnType.DatetimeTooltip:
                return getDateTimeTooltipColumn(colDef);
            default:
                return getTooltipColumn(colDef);
        }
    }) ?? [];

    const DATA_GRID_ROW_CURSOR_INLINE_STYLES = rowClick && {
        '& .table-cell-truncate': { cursor: 'pointer' }
    };

    const noRowsOverlayTitle = noRowsOverlayCustomTitle ?? title;
    const contentFunctionTitleToRender = customCreateEditTitle ?? title;

    return (
        <>
            <StyledDataGrid
                getRowSpacing={(params: GridRowSpacingParams) => ({
                    top: params.isFirstVisible ? 2 : 3,
                    bottom: params.isLastVisible ? 1 : 0
                })}
                rowCount={processing ? 0 : rows.length}
                loading={processing}
                columnHeaderHeight={42}
                rows={processing ? [] : rows || []}
                columns={columns}
                autoHeight
                disableColumnMenu={true}
                hideFooter={true}
                slots={{
                    noRowsOverlay: CustomNoRowsOverlay,
                }}
                sx={DATA_GRID_ROW_CURSOR_INLINE_STYLES}
                slotProps={{
                    noRowsOverlay: {
                        title: noRowsOverlayTitle ?? '',
                        useCustomTitle: noRowsOverlayCustomTitle ? noRowsOverlayCustomTitle?.length > 0 : undefined
                    }
                }}
                paginationMode={PaginationMode.Server}
                sortingMode={PaginationMode.Client}
                onSortModelChange={clientSortHandler}
                sortModel={sortModel}
            />
            {contentFunction?.({
                title: contentFunctionTitleToRender ?? '',
                tenant,
                editingItemInfo: {
                    editingItemId,
                    editingRow: editingRow as T,
                }
            })}
            {deleteFunction?.({
                title,
                customDeleteTitle,
                tenant,
                itemId: editingItemId,
                editingRow: editingRow as K,
            })}
        </>
    );
}
